import React from 'react'
import classNames from 'classnames'

import { withStyles, createStyles, WithStyles } from '@material-ui/core'

interface Props {
  primaryColor?: string
  secondaryColor?: string
  duration?: number
  delay?: number
  className?: string
  onCircleAnimationEnd?: () => void
  onCheckmarkAnimationEnd?: () => void
  iconUrl?: string
  iconOpacity?: number
  checkmark?: boolean
}

type State = {
  checkmarkElement: SVGPathElement | null
  progressElement: SVGCircleElement | null
}

const styles = createStyles({
  root: {
    transform: 'rotate(-90deg)',
  },
  circle: {
    animationName: '$AnimatedCircle-Circle-Animation',
    animationTimingFunction: 'ease-in-out',
    animationFillMode: 'forwards',
    strokeDashoffset: 351.8583773,
    strokeDasharray: 351.858377,
  },
  show: {
    opacity: 0,
    animationName: '$AnimatedCircle-Show-Animation',
    animationTimingFunction: 'ease',
    animationFillMode: 'forwards',
    animationDuration: '250ms',
  },
  checkmark: {
    opacity: 0,
    animationName: '$AnimatedCircle-Checkmark-Animation',
    animationTimingFunction: 'ease',
    animationFillMode: 'forwards',
    animationDuration: '500ms',
    strokeDashoffset: 71,
    strokeDasharray: 71,
  },
  '@keyframes AnimatedCircle-Circle-Animation': {
    from: { strokeDashoffset: 351.858377 },
    to: { strokeDashoffset: 0 },
  },
  '@keyframes AnimatedCircle-Show-Animation': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  '@keyframes AnimatedCircle-Checkmark-Animation': {
    '0%': {
      strokeDashoffset: 71,
      opacity: 0,
    },
    '50%': {
      strokeDashoffset: 71,
      opacity: 1,
    },
    '100%': {
      strokeDashoffset: 0,
      opacity: 1,
    },
  },
})

type InjectedProps = Props & WithStyles<typeof styles>

class AnimatedCircle extends React.Component<InjectedProps, State> {
  constructor(props: InjectedProps) {
    super(props)
    this.state = { checkmarkElement: null, progressElement: null }
  }

  static get defaultProps() {
    return {
      primaryColor: '#ffffff',
      secondaryColor: '#0BEEB6',
      duration: 1000,
      delay: 0,
    }
  }

  componentDidUpdate() {
    if (this.props.onCircleAnimationEnd && this.state.progressElement) {
      this.state.progressElement.addEventListener('animationend', this.props.onCircleAnimationEnd)
    }

    if (this.props.onCheckmarkAnimationEnd && this.state.checkmarkElement) {
      this.state.checkmarkElement.addEventListener('animationend', this.props.onCheckmarkAnimationEnd)
    }
  }

  render() {
    const {
      primaryColor,
      secondaryColor,
      iconUrl,
      iconOpacity,
      duration,
      delay,
      checkmark,
      classes,
      className,
    } = this.props

    return (
      <svg
        width="120"
        height="120"
        viewBox="0 0 120 120"
        className={classNames(classes.root, className)}
      >
        <circle
          className={classes.circle}
          style={{ animationDuration: `${duration}ms`, animationDelay: `${delay}ms` }}
          cx="60"
          cy="60"
          r="56"
          fill="none"
          stroke={secondaryColor}
          strokeWidth="8"
          ref={(progressElement) => { if (!this.state.progressElement) { this.setState({ progressElement }) } }}
        />
        <circle
          cx="60"
          cy="60"
          r="53"
          fill={primaryColor}
        />
        {
          iconUrl &&
          <image
            xlinkHref={iconUrl}
            x="50%"
            y="50%"
            width="54"
            height="54"
            transform="rotate(90, 60, 60) translate(-27, -27)"
            opacity={iconOpacity ? +iconOpacity : 1}
          />
        }
        {
          checkmark && [
            <circle
              key={0}
              className={classes.show}
              cx="60"
              cy="60"
              r="56"
              fill="none"
              stroke={primaryColor}
              strokeWidth="8"
            />,
            <circle
              key={1}
              className={classes.show}
              cx="60"
              cy="60"
              r="53"
              fill={secondaryColor}
            />,
            <path
              key={2}
              className={classes.checkmark}
              fill="none"
              stroke={primaryColor}
              strokeWidth="8"
              d="M 58 37 L 43 52 L 78 87"
              ref={(checkmarkElement) => { if (!this.state.checkmarkElement) { this.setState({ checkmarkElement }) } }}
            />,
          ]
        }
      </svg>
    )
  }
}

export default withStyles(styles)(AnimatedCircle)
